// Uploader de screenshots con paste-from-clipboard + drag-drop + multi-file.
// Trabaja con File objects en local (staging) hasta que el trade se cree;
// recién después se suben uno por uno al endpoint POST /trades/:id/screenshots.
//
// Por screenshot el usuario puede asignar timeframe (1D, 4H, 1H, 15M, etc.)
// y phase (pre/entry/post) opcional.

import { useEffect, useRef, useState, type ReactElement } from 'react';
import type { Timeframe } from '@bini-trading/shared-types';
import { cn } from '../../lib/cn';

export interface StagedScreenshot {
  // ID local solo para keys de React
  localId: string;
  file: File;
  preview: string; // object URL
  timeframe: Timeframe | null;
  phase: 'pre' | 'entry' | 'post' | null;
}

const TIMEFRAMES: ReadonlyArray<{ value: Timeframe; label: string }> = [
  { value: '1d', label: '1D' },
  { value: '4h', label: '4H' },
  { value: '1h', label: '1H' },
  { value: '15m', label: '15M' },
  { value: '5m', label: '5M' },
  { value: '1m', label: '1M' },
];

const PHASES: ReadonlyArray<{ value: 'pre' | 'entry' | 'post'; label: string }> = [
  { value: 'pre', label: 'Pre' },
  { value: 'entry', label: 'Entry' },
  { value: 'post', label: 'Post' },
];

interface Props {
  items: StagedScreenshot[];
  onChange: (items: StagedScreenshot[]) => void;
}

export function ScreenshotUploader({ items, onChange }: Props): ReactElement {
  const dropRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const stageFiles = (files: FileList | File[]): void => {
    const accepted: StagedScreenshot[] = [];
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 10 * 1024 * 1024) continue;
      accepted.push({
        localId: `local_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        file,
        preview: URL.createObjectURL(file),
        timeframe: null,
        phase: null,
      });
    }
    if (accepted.length > 0) onChange([...items, ...accepted]);
  };

  // Paste desde portapapeles (Ctrl+V). Captura globalmente cuando el modal
  // está abierto. El handler se monta solo cuando este componente lo está.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent): void => {
      const cd = e.clipboardData;
      if (!cd) return;
      const files: File[] = [];
      for (const item of Array.from(cd.items)) {
        if (item.kind === 'file') {
          const f = item.getAsFile();
          if (f && f.type.startsWith('image/')) files.push(f);
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        stageFiles(files);
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Liberar object URLs al desmontar.
  useEffect(() => {
    return () => {
      items.forEach((it) => URL.revokeObjectURL(it.preview));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateItem = (localId: string, patch: Partial<StagedScreenshot>): void => {
    onChange(items.map((it) => (it.localId === localId ? { ...it, ...patch } : it)));
  };

  const removeItem = (localId: string): void => {
    const target = items.find((it) => it.localId === localId);
    if (target) URL.revokeObjectURL(target.preview);
    onChange(items.filter((it) => it.localId !== localId));
  };

  return (
    <div className="space-y-3">
      <div
        ref={dropRef}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (e.currentTarget === e.target) setIsDragging(false);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(false);
          if (e.dataTransfer?.files) stageFiles(e.dataTransfer.files);
        }}
        className={cn(
          'rounded-lg border-2 border-dashed p-5 sm:p-6 text-center transition-colors',
          isDragging
            ? 'border-brand-500 bg-brand-500/5'
            : 'border-surface-border bg-surface-panel/30 hover:border-surface-strong',
        )}
      >
        <div className="text-3xl text-ink-faint mb-2">📷</div>

        {/* Botón principal — funciona en mobile y desktop. En mobile abre la cámara/galería */}
        <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-500 hover:bg-brand-600 text-white font-medium text-sm cursor-pointer transition-colors shadow-md shadow-brand-500/20 mb-3">
          <span>+ Subir imágenes</span>
          <input
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) stageFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </label>

        {/* Hint desktop — paste / drag. En mobile esto es menos relevante */}
        <div className="text-xs text-ink-subtle hidden sm:block">
          o pegá con <kbd className="px-1.5 py-0.5 rounded bg-surface-elevated border border-surface-border font-mono text-[10px]">Ctrl+V</kbd> · arrastrá archivos acá
        </div>
        <div className="text-[10px] text-ink-faint mt-1">
          PNG, JPG, WEBP, GIF · máx 10 MB cada una
        </div>
      </div>

      {items.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {items.map((it) => (
            <div
              key={it.localId}
              className="rounded-lg border border-surface-border bg-surface-elevated overflow-hidden"
            >
              <div className="relative aspect-video bg-black">
                <img
                  src={it.preview}
                  alt=""
                  className="w-full h-full object-contain"
                />
                <button
                  type="button"
                  onClick={() => removeItem(it.localId)}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 hover:bg-loss-DEFAULT text-white text-xs flex items-center justify-center transition-colors"
                  title="Quitar"
                >
                  ×
                </button>
              </div>
              <div className="p-2 space-y-1.5">
                <div className="flex flex-wrap gap-1">
                  {TIMEFRAMES.map((tf) => (
                    <button
                      key={tf.value}
                      type="button"
                      onClick={() =>
                        updateItem(it.localId, {
                          timeframe: it.timeframe === tf.value ? null : tf.value,
                        })
                      }
                      className={cn(
                        'text-[10px] font-mono px-1.5 py-0.5 rounded border transition-colors',
                        it.timeframe === tf.value
                          ? 'bg-brand-500/15 border-brand-500/50 text-brand-500 font-semibold'
                          : 'border-surface-border text-ink-faint hover:text-ink-muted hover:border-surface-strong',
                      )}
                    >
                      {tf.label}
                    </button>
                  ))}
                </div>
                <div className="flex gap-1">
                  {PHASES.map((p) => (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() =>
                        updateItem(it.localId, {
                          phase: it.phase === p.value ? null : p.value,
                        })
                      }
                      className={cn(
                        'flex-1 text-[10px] font-medium px-1.5 py-0.5 rounded border transition-colors',
                        it.phase === p.value
                          ? 'bg-info-DEFAULT/15 border-info-DEFAULT/50 text-info-DEFAULT font-semibold'
                          : 'border-surface-border text-ink-faint hover:text-ink-muted hover:border-surface-strong',
                      )}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
